import { useLocation, useParams } from "@tanstack/react-router";
import { useAdminMerchant } from "@/api/admin/merchant";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { TabPanel, type VerticalTab, VerticalTabs } from "@/components/application/vertical-tabs/vertical-tabs";
import {
  AllUsersTab,
  CompanyDetailsTab,
  CopySequencesTab,
  IntegrationsTab,
  PermissionsTab,
  SafetyTab,
  WorkspacesTab,
} from "./tabs";

const MERCHANT_TABS: VerticalTab[] = [
  { id: "company-details", label: "Merchant Details" },
  { id: "permissions", label: "Permissions" },
  { id: "risk-configuration", label: "Safety Configuration" },
  { id: "integrations", label: "Integrations" },
  { id: "workspaces", label: "Workspaces" },
  { id: "all-users", label: "All Users" },
  { id: "copy-sequences", label: "Copy Sequences" },
];

export const MerchantDetailPage = () => {
  const { id } = useParams({ strict: false }) as { id: string };
  const location = useLocation();
  const { data: merchant, isLoading } = useAdminMerchant(id);

  const rawTab = location.hash || MERCHANT_TABS[0].id;
  // The old safety-models tab was merged into the Safety tab — keep its links working.
  const activeTab = rawTab === "safety-models" ? "risk-configuration" : rawTab;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-tertiary">Loading...</p>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-tertiary">Merchant not found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col mt-4 px-12">
      <div className="flex flex-row items-center gap-4">
        <MerchantLogo name={merchant.name} logoUrl={merchant.logoUrl} size="lg" />
        <div className="flex flex-col">
          <h1 className="text-xl font-semibold text-primary">{merchant.name}</h1>
          <p className="text-md text-tertiary">ID: {merchant.id}</p>
        </div>
      </div>

      <hr className="border-tertiary mt-4 mb-8" />

      <VerticalTabs tabs={MERCHANT_TABS} basePath={`/admin/merchants/${id}`}>
        <TabPanel id="company-details" activeTab={activeTab}>
          <CompanyDetailsTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="permissions" activeTab={activeTab}>
          <PermissionsTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="risk-configuration" activeTab={activeTab}>
          <SafetyTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="integrations" activeTab={activeTab}>
          <IntegrationsTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="workspaces" activeTab={activeTab}>
          <WorkspacesTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="all-users" activeTab={activeTab}>
          <AllUsersTab merchant={merchant} />
        </TabPanel>
        <TabPanel id="copy-sequences" activeTab={activeTab}>
          <CopySequencesTab merchant={merchant} />
        </TabPanel>
      </VerticalTabs>
    </div>
  );
};
